import type { EntityType } from "@repo/game-admin"
import type { SupportedGames } from "@repo/game-configuration"
import {
	Configurations,
	commonAttributeTranslationsMap,
	gameTranslationsMap,
} from "@repo/game-configuration"
import { Locale } from "@repo/i18n"

/**
 * Resolves a translation key to its English text using game and common translations
 */
function resolveTranslationKey(key: string, gameSlug: string): string {
	// EG: game-specific keys like "lorcana.attributes.rarity.values.common"
	if (key.startsWith(`${gameSlug}.attributes.`)) {
		const gameTranslations = gameTranslationsMap[gameSlug]?.[
			Locale.English
		] as Record<string, unknown>
		if (gameTranslations) {
			const keyWithoutGamePrefix = key.replace(`${gameSlug}.`, "")
			const gameValue = getNestedValue(gameTranslations, keyWithoutGamePrefix)

			if (gameValue) {
				return gameValue
			}
		}
	}

	// Handle global common attribute keys like "global.attributes.finish.values.standard"
	if (key.startsWith("global.attributes.")) {
		const commonTranslations = commonAttributeTranslationsMap[
			Locale.English
		] as Record<string, unknown>
		if (commonTranslations) {
			const keyWithoutGlobal = key.replace("global.", "")
			const commonValue = getNestedValue(commonTranslations, keyWithoutGlobal)
			if (commonValue) {
				return commonValue
			}
		}
	}

	const gameTranslations = gameTranslationsMap[gameSlug]?.[
		Locale.English
	] as Record<string, unknown>
	if (gameTranslations) {
		const gameValue = getNestedValue(gameTranslations, key)
		if (gameValue) {
			return gameValue
		}
	}

	return key
}

/**
 * Helper function to get nested value from object using dot notation
 */
function getNestedValue(
	obj: Record<string, unknown>,
	path: string,
): string | null {
	const keyParts = path.split(".")
	let value: unknown = obj

	for (const part of keyParts) {
		if (value && typeof value === "object" && value !== null && part in value) {
			value = (value as Record<string, unknown>)[part]
		} else {
			return null
		}
	}

	return typeof value === "string" ? value : null
}

export function formatGameName(slug: string): string {
	const gameConfig = Object.values(Configurations).find(
		(config) => config.slug === slug,
	)

	if (gameConfig) {
		return gameConfig.name
	}

	return slug.charAt(0).toUpperCase() + slug.slice(1)
}

/**
 * Format rarity to display name based on game
 * Gets the display name from the game configuration attributes
 */
export function formatRarityName(rarity: string, gameSlug?: string): string {
	if (!gameSlug) {
		return rarity
			.replace(/[_-]/g, " ")
			.split(" ")
			.map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
			.join(" ")
	}

	const gameConfig = Configurations[gameSlug as SupportedGames]
	if (!gameConfig?.attributes?.rarity) {
		return rarity
	}

	const rarityAttribute = gameConfig.attributes.rarity as {
		values?: Record<string, { label?: string }>
	}
	if (!rarityAttribute.values) {
		return rarity
	}

	const rarityValues = rarityAttribute.values
	const matchingRarity = Object.entries(rarityValues).find(
		([key, value]) =>
			key.toLowerCase() === rarity.toLowerCase() ||
			(value?.label && value.label.toLowerCase() === rarity.toLowerCase()),
	)

	if (matchingRarity?.[1]?.label) {
		const label = matchingRarity[1].label
		if (typeof label === "string" && label.includes(".")) {
			return resolveTranslationKey(label, gameSlug)
		}
		return label
	}

	return rarity
}

/**
 * Format finish to display name based on game
 * Gets the display name from the game configuration attributes
 */
export function formatFinishName(finish: string, gameSlug?: string): string {
	if (!gameSlug) {
		return finish
			.replace(/[_-]/g, " ")
			.split(" ")
			.map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
			.join(" ")
	}

	const gameConfig = Configurations[gameSlug as SupportedGames]
	if (!gameConfig?.attributes?.finish) {
		return finish
	}

	const finishAttribute = gameConfig.attributes.finish as {
		values?: Record<string, { label?: string }>
	}
	if (!finishAttribute.values) {
		return finish
	}

	const finishValues = finishAttribute.values
	const matchingFinish = Object.entries(finishValues).find(
		([key, value]) =>
			key.toLowerCase() === finish.toLowerCase() ||
			(value?.label && value.label.toLowerCase() === finish.toLowerCase()),
	)

	if (matchingFinish?.[1]?.label) {
		const label = matchingFinish[1].label
		if (typeof label === "string" && label.includes(".")) {
			return resolveTranslationKey(label, gameSlug)
		}
		return label
	}

	return finish
}

export const resolveEntityTypeLabel = (entityType: string): string => {
	switch (entityType) {
		case "card":
			return "Product"
		case "group":
			return "Expansion Group"
		case "expansion":
			return "Expansion"
		case "game":
			return "Game"
		default:
			return entityType
	}
}

/**
 * Maps tree node types to API entity types
 * (card -> product, group -> expansionGroup)
 */
export const resolveEntityType = (nodeType: string): EntityType => {
	switch (nodeType) {
		case "card":
			return "product"
		case "group":
			return "expansionGroup"
		case "expansion":
			return "expansion"
		case "game":
			return "game"
		default:
			return "product"
	}
}

/**
 * External ID value per marketplace: [{ finish: "Standard", id: 123 }, { finish: "Foil", id: 456 }]
 */
export type ExternalIdValue =
	| Array<{ finish: string; id: number }>
	| null
	| undefined

/**
 * Format external IDs for display.
 * Returns an array of { finish, id } entries for rendering.
 */
export function formatExternalIds(
	value: ExternalIdValue,
): Array<{ finish: string; id: number }> {
	if (!value || !Array.isArray(value)) return []

	return value.filter((entry) => entry && typeof entry.id === "number")
}

/**
 * Get a single external ID for display (first available)
 */
export function getFirstExternalId(value: ExternalIdValue): number | null {
	const ids = formatExternalIds(value)
	return ids[0]?.id ?? null
}
